import { useState, useMemo } from 'react';

const STATUS_META = {
  new:       { label: 'New',       color: '#0C447C' },
  hot:       { label: 'Hot',       color: '#A32D2D' },
  warm:      { label: 'Warm',      color: '#633806' },
  cold:      { label: 'Cold',      color: '#27500A' },
  converted: { label: 'Converted', color: '#085041' },
  lost:      { label: 'Lost',      color: '#5F5E5A' },
  followup:  { label: 'Followup',  color: '#854F0B' },
};

const SOURCE_LABEL = { whatsapp: 'Get Quote', website: 'Website', agent: 'Agent', ads: 'Meta Ad', manual: 'Manual' };

function MiniBar({ label, value, max, color }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#444', marginBottom: 3 }}>
        <span>{label}</span><span style={{ color: '#888' }}>{value}</span>
      </div>
      <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(4, (value / Math.max(1, max)) * 100)}%`, height: '100%', background: color || '#25d366', borderRadius: 4 }} />
      </div>
    </div>
  );
}

function exportCSV(rows, headers, filename) {
  const esc = v => { const s = String(v ?? '').replace(/"/g, '""'); return /[",\n]/.test(s) ? `"${s}"` : s; };
  const csv = [headers, ...rows.map(r => headers.map(h => esc(r[h])))].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

export function LeadReports({ leads, agents }) {
  const [groupBy, setGroupBy] = useState('status');
  const [dateRange, setDateRange] = useState('all');
  const [schedEnabled, setSchedEnabled] = useState(false);
  const [schedFreq, setSchedFreq] = useState('daily');
  const [schedChannel, setSchedChannel] = useState('whatsapp');
  const [schedRecipient, setSchedRecipient] = useState('');
  const [schedSaved, setSchedSaved] = useState(false);

  const filtered = useMemo(() => {
    const now = new Date();
    return leads.filter(l => {
      if (dateRange === 'today') return new Date(l.created_at).toDateString() === now.toDateString();
      if (dateRange === '7d') return now - new Date(l.created_at) <= 7 * 86400000;
      if (dateRange === '30d') return now - new Date(l.created_at) <= 30 * 86400000;
      return true;
    });
  }, [leads, dateRange]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach(l => {
      let key = '';
      if (groupBy === 'status') key = l.status || 'new';
      else if (groupBy === 'source') key = l.source || 'manual';
      else if (groupBy === 'agent') key = l.agent_name || 'Unassigned';
      else if (groupBy === 'product') key = l.product_tag || 'No Product';
      const e = map.get(key) || { key, total: 0, converted: 0, hot: 0, lost: 0 };
      e.total++;
      if (l.status === 'converted') e.converted++;
      if (l.status === 'hot') e.hot++;
      if (l.status === 'lost') e.lost++;
      map.set(key, e);
    });
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [filtered, groupBy]);

  const summary = useMemo(() => ({
    total: filtered.length,
    converted: filtered.filter(l => l.status === 'converted').length,
    hot: filtered.filter(l => l.status === 'hot').length,
    lost: filtered.filter(l => l.status === 'lost').length,
    convRate: filtered.length ? Math.round((filtered.filter(l => l.status === 'converted').length / filtered.length) * 100) : 0,
  }), [filtered]);

  const maxTotal = Math.max(1, ...groups.map(g => g.total));

  const colorOf = (key) => {
    if (groupBy === 'status') return STATUS_META[key]?.color || '#25d366';
    const colors = ['#25d366','#075e54','#1565c0','#6a1b9a','#e65100','#c62828','#2e7d32'];
    const idx = groups.findIndex(g => g.key === key);
    return colors[idx % colors.length];
  };

  const doExport = () => {
    const headers = ['Group', 'Total', 'Converted', 'Hot', 'Lost', 'Conv%'];
    const rows = groups.map(g => ({
      'Group': g.key,
      'Total': g.total,
      'Converted': g.converted,
      'Hot': g.hot,
      'Lost': g.lost,
      'Conv%': g.total ? Math.round((g.converted / g.total) * 100) + '%' : '0%',
    }));
    exportCSV(rows, headers, `lead_report_${groupBy}_${dateRange}_${new Date().toISOString().slice(0,10)}.csv`);
  };

  const btnStyle = (active) => ({
    padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', borderRadius: 20,
    background: active ? '#075e54' : '#f0f0f0', color: active ? '#fff' : '#555', fontFamily: 'inherit',
  });

  const saveSchedule = () => {
    setSchedSaved(true);
    setTimeout(() => setSchedSaved(false), 2500);
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>📊 Lead Reports</div>
        <button onClick={doExport} style={{ padding: '8px 16px', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          ⬇ Export CSV
        </button>
      </div>

      {/* Scheduled Reports */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', border: '1px solid #f0f0f0', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: schedEnabled ? 14 : 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e' }}>📅 Scheduled Reports</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <span style={{ fontSize: 11, color: '#999', fontWeight: 600 }}>{schedEnabled ? 'Enabled' : 'Disabled'}</span>
            <div onClick={() => setSchedEnabled(v => !v)} style={{ width: 36, height: 20, borderRadius: 10, background: schedEnabled ? '#25d366' : '#ddd', position: 'relative', transition: 'background .2s' }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: schedEnabled ? 18 : 2, transition: 'left .2s' }} />
            </div>
          </label>
        </div>

        {schedEnabled && (
          <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: '#aaa', fontWeight: 700 }}>FREQUENCY:</span>
              {[['daily','Daily'],['weekly','Weekly'],['monthly','Monthly']].map(([k,l]) => (
                <button key={k} style={btnStyle(schedFreq === k)} onClick={() => setSchedFreq(k)}>{l}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: '#aaa', fontWeight: 700 }}>SEND VIA:</span>
              {[['whatsapp','WhatsApp'],['email','Email']].map(([k,l]) => (
                <button key={k} style={btnStyle(schedChannel === k)} onClick={() => setSchedChannel(k)}>{l}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={schedRecipient}
                onChange={e => setSchedRecipient(e.target.value)}
                placeholder={schedChannel === 'whatsapp' ? 'WhatsApp number' : 'Email address'}
                style={{ flex: 1, minWidth: 180, padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
              />
              <button onClick={saveSchedule} style={{ padding: '8px 18px', background: '#075e54', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Save Schedule
              </button>
              {schedSaved && <span style={{ fontSize: 12, color: '#25d366', fontWeight: 700 }}>✅ Saved (preview mode — backend integration pending)</span>}
            </div>
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8, marginBottom: 20 }}>
        {[
          { label: 'Total', value: summary.total, color: '#1a1a2e' },
          { label: 'Converted', value: summary.converted, color: '#085041' },
          { label: 'Hot', value: summary.hot, color: '#A32D2D' },
          { label: 'Lost', value: summary.lost, color: '#5F5E5A' },
          { label: 'Conv %', value: summary.convRate + '%', color: '#25d366' },
        ].map(c => (
          <div key={c.label} style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #f0f0f0', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', border: '1px solid #f0f0f0', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#aaa', fontWeight: 700 }}>GROUP BY:</span>
          {['status','source','agent','product'].map(g => (
            <button key={g} style={btnStyle(groupBy === g)} onClick={() => setGroupBy(g)}>{g}</button>
          ))}
          <span style={{ fontSize: 11, color: '#aaa', fontWeight: 700, marginLeft: 8 }}>RANGE:</span>
          {[['all','All time'],['today','Today'],['7d','7 days'],['30d','30 days']].map(([k,l]) => (
            <button key={k} style={btnStyle(dateRange === k)} onClick={() => setDateRange(k)}>{l}</button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #f0f0f0', marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 14, textTransform: 'uppercase' }}>
          Leads by {groupBy}
        </div>
        {groups.length === 0 && <div style={{ color: '#bbb', fontSize: 12 }}>No data</div>}
        {groups.map(g => (
          <MiniBar key={g.key} label={SOURCE_LABEL[g.key] || g.key} value={g.total} max={maxTotal} color={colorOf(g.key)} />
        ))}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eef0f2', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              {['Group','Total','Converted','Hot','Lost','Conv %'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map(g => (
              <tr key={g.key} style={{ borderBottom: '1px solid #f8f8f8' }}>
                <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700, color: colorOf(g.key) }}>
                  {groupBy === 'status' ? (STATUS_META[g.key]?.label || g.key) : (SOURCE_LABEL[g.key] || g.key)}
                </td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#333' }}>{g.total}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#085041' }}>{g.converted}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#A32D2D' }}>{g.hot}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#5F5E5A' }}>{g.lost}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#25d366', fontWeight: 700 }}>
                  {g.total ? Math.round((g.converted / g.total) * 100) : 0}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
